import { Injectable, OnDestroy, effect, signal, untracked } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { SessionService } from '../account-service/session.service';
import { AppSettingsService } from '../app-settings.service';
import { clubUserActionsInvalidation, householdUserActionsInvalidation, readRealtimeEnvelope, RealtimeEnvelope } from './realtime-message';

interface RealtimeIdentity {
  token: string;
  version: number;
  accountId: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeService implements OnDestroy {
  // Private variables

  private connection?: signalR.HubConnection;
  private identity?: RealtimeIdentity;
  private generation = 0;
  private readonly requestedClubs = new Set<string>();
  private readonly requestedHouseholds = new Map<string, number>();
  private connecting = false;
  private connectionAttempts = 0;
  private readonly onAvailable = () => {
    if (typeof document === 'undefined' || !document.hidden) void this.ensureConnected();
  };

  // Public properties

  public readonly connected = signal(false);
  public readonly notifications = new Subject<RealtimeEnvelope>();
  public readonly userActionsChanged = new Subject<void>();
  public readonly recovered = new Subject<void>();

  // Constructors

  constructor(private readonly session: SessionService, private readonly settings: AppSettingsService) {
    effect(() => {
      const account = this.session.account();
      const status = this.session.status();
      const version = this.session.version;
      const token = this.session.currentToken;

      // A restoring account must not negotiate or retain subscriptions from the previous account.
      const identity = status === 'authenticated' && account?.id && token
        ? { token, version, accountId: account.id } : undefined;

      untracked(() => this.switchAccount(identity));
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onAvailable);
      document.addEventListener('visibilitychange', this.onAvailable);
    }
  }

  // Public methods

  public ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onAvailable);
      document.removeEventListener('visibilitychange', this.onAvailable);
    }
    this.stop();
  }


  public watchClub(clubId: string): () => void {
    const id = clubId.toLowerCase();
    this.requestedClubs.add(id);
    void this.restoreClubs();

    return () => {
      if (!this.requestedClubs.delete(id)) return;
      const connection = this.connection;
      if (connection?.state === signalR.HubConnectionState.Connected)
        void connection.send('UnsubscribeFromClub', id).catch(() => {});
    };
  }

  public watchHousehold(householdId: string): () => void {
    const id = householdId.toLowerCase();
    this.requestedHouseholds.set(id, (this.requestedHouseholds.get(id) || 0) + 1);
    void this.restoreHouseholds();

    return () => {
      const count = this.requestedHouseholds.get(id);

      if (!count)
        return;

      if (count > 1) {
        this.requestedHouseholds.set(id, count - 1);
        return;
      }

      this.requestedHouseholds.delete(id);
      const connection = this.connection;

      if (connection?.state === signalR.HubConnectionState.Connected)
        void connection.send('UnsubscribeFromHousehold', id).catch(() => {});
    };
  }

  public stop(): void {
    // Invalidate synchronously: messages from the old connection are rejected before stop resolves.
    this.generation++;
    this.identity = undefined;
    this.requestedClubs.clear();
    this.requestedHouseholds.clear();
    this.connected.set(false);
    this.connecting = false;
    this.connectionAttempts = 0;
    const connection = this.connection;
    this.connection = undefined;
    if (connection) void connection.stop().catch(() => {});
  }

  // Private methods

  private switchAccount(identity: RealtimeIdentity | undefined): void {
    if (identity && this.identity && this.sameIdentity(identity, this.identity)) return;

    const sameAccount = !!identity && this.identity?.accountId === identity.accountId;
    const clubs = sameAccount ? [...this.requestedClubs] : [];
    const households = sameAccount ? [...this.requestedHouseholds] : [];

    this.stop();
    if (!identity) return;

    // Refreshing a token replaces the connection, not the visible workspace's subscriptions.
    clubs.forEach(clubId => this.requestedClubs.add(clubId));
    households.forEach(([householdId, count]) => this.requestedHouseholds.set(householdId, count));

    const version = this.generation;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.settings.apiPath}hubs/NotificationHub`, {
        accessTokenFactory: () => this.session.isCurrent(identity.token, identity.version) &&
          this.session.status() === 'authenticated' && this.identity?.accountId === identity.accountId ? identity.token : ''
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.identity = identity;
    this.connection = connection;

    connection.on('Notification', (message: unknown) => {
      if (!this.isCurrent(connection, identity, version)) return;
      const envelope = readRealtimeEnvelope(message);
      if (!envelope) return;

      this.notifications.next(envelope);
      if (clubUserActionsInvalidation(envelope) || householdUserActionsInvalidation(envelope))
        this.userActionsChanged.next();
    });

    connection.on('UserAction', (message: unknown) => {
      if (!this.isCurrent(connection, identity, version)) return;
      if (readRealtimeEnvelope(message)) this.userActionsChanged.next();
    });

    connection.onreconnecting(() => {
      if (this.isCurrent(connection, identity, version)) this.connected.set(false);
    });

    connection.onreconnected(() => {
      if (!this.isCurrent(connection, identity, version)) return;
      this.connected.set(true);
      void this.restoreClubs();
      void this.restoreHouseholds();
      this.recovered.next();
    });

    connection.onclose(() => {
      if (this.isCurrent(connection, identity, version)) this.connected.set(false);
    });

    void this.ensureConnected();
  }

  private async ensureConnected(): Promise<void> {
    const connection = this.connection;
    const identity = this.identity;
    const version = this.generation;

    if (!connection || !identity || this.connecting || !this.isCurrent(connection, identity, version) ||
        connection.state !== signalR.HubConnectionState.Disconnected) return;

    this.connecting = true;
    const attempt = ++this.connectionAttempts;
    try {
      await connection.start();
      if (!this.isCurrent(connection, identity, version)) {
        await connection.stop();
        return;
      }
      this.connected.set(true);
      await this.restoreClubs();
      await this.restoreHouseholds();
      if (attempt > 1) this.recovered.next();
    } catch {
      // A failed initial negotiate is retried when the browser comes back online or is focused.
      if (this.isCurrent(connection, identity, version)) this.connected.set(false);
    } finally {
      if (this.isCurrent(connection, identity, version)) this.connecting = false;
    }
  }

  private async restoreClubs(): Promise<void> {
    const connection = this.connection;
    const identity = this.identity;
    const version = this.generation;

    if (!connection || !identity || !this.isCurrent(connection, identity, version) ||
        connection.state !== signalR.HubConnectionState.Connected || !this.requestedClubs.size) return;

    try {
      await connection.send('SubscribeToClubs', [...this.requestedClubs]);
    } catch {
      // Staff-group subscriptions are permissions-checked by the server.
    }
  }

  private async restoreHouseholds(): Promise<void> {
    const connection = this.connection;
    const identity = this.identity;
    const version = this.generation;

    if (!connection || !identity || !this.isCurrent(connection, identity, version) ||
        connection.state !== signalR.HubConnectionState.Connected || !this.requestedHouseholds.size)
      return;

    try {
      await connection.send('SubscribeToHouseholds', [...this.requestedHouseholds.keys()]);
    } catch {
      // Active membership is checked on the server. Event delivery also checks current permissions.
    }
  }

  private isCurrent(connection: signalR.HubConnection, identity: RealtimeIdentity, version: number): boolean {
    return this.connection === connection && version === this.generation &&
      this.identity !== undefined && this.sameIdentity(identity, this.identity) &&
      this.session.isCurrent(identity.token, identity.version) &&
      this.session.status() === 'authenticated' && this.session.account()?.id === identity.accountId;
  }

  private sameIdentity(left: RealtimeIdentity, right: RealtimeIdentity): boolean {
    return left.token === right.token && left.version === right.version && left.accountId === right.accountId;
  }
}
